import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Send } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import api from '../services/api';

/**
 * Build Prompt 9 — additive/test-mode Report Pack distribution.
 * Separate from (and never wired into) the existing Summary Report /
 * Client Portal distribution flow.
 */
export default function SendReportPackDialog({ open, onOpenChange, sessionId, suggestedRecipient, onSent }) {
  const { toast } = useToast();
  const [recipients, setRecipients] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [includePdf, setIncludePdf] = useState(true);
  const [includeExcel, setIncludeExcel] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [sendErrorCode, setSendErrorCode] = useState(null);

  useEffect(() => {
    if (open) {
      setRecipients(suggestedRecipient || '');
      setSubject('');
      setMessage('');
      setIncludePdf(true);
      setIncludeExcel(false);
      setSendError(null);
      setSendErrorCode(null);
    }
  }, [open, suggestedRecipient]);

  const send = async () => {
    setSendError(null);
    setSendErrorCode(null);
    const recipientList = recipients.split(',').map(r => r.trim()).filter(Boolean);
    if (recipientList.length === 0) {
      toast({ title: 'Add at least one recipient email', variant: 'destructive' });
      return;
    }
    if (!includePdf && !includeExcel) {
      toast({ title: 'Select PDF Pack and/or Excel Pack', variant: 'destructive' });
      return;
    }
    setSending(true);
    try {
      const res = await api.post(`/stocktake/sessions/${sessionId}/report-packs/send`, {
        recipients: recipientList, subject: subject || undefined, message,
        include_pdf: includePdf, include_excel: includeExcel,
      });
      toast({ title: 'Report Pack sent', description: `Sent to ${recipientList.join(', ')}` });
      onOpenChange(false);
      onSent && onSent(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = (detail && typeof detail === 'object') ? detail.code : null;
      const errMessage = (detail && typeof detail === 'object') ? detail.message : (detail || err.message);
      setSendError(errMessage);
      setSendErrorCode(code);
      toast({ title: 'Send failed', description: errMessage, variant: 'destructive' });
      // Only pass version data back if the email itself failed (5xx) — a
      // 422 means no pack exists yet so there's nothing new to reflect.
      if (err?.response?.status !== 422) {
        onSent && onSent(detail && typeof detail === 'object' ? detail : null);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="send-report-pack-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send className="h-4 w-4" />Send Report Pack</DialogTitle>
          <DialogDescription>Sends the latest generated version of the pack. Generate the PDF or Excel pack first via the Report Centre, then use this to distribute it.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {sendError && (
            <div className="rounded-md border border-red-200 bg-red-50 p-2.5 text-xs text-red-800" data-testid="send-pack-error-banner">
              <strong>Send failed:</strong> {sendError}
              {(sendErrorCode === 'no_pdf_pack' || sendErrorCode === 'no_xlsx_pack') ? (
                <p className="text-red-600 mt-1">Close this dialog, use the Report Centre to generate the pack, then return here to send it.</p>
              ) : (
                <p className="text-red-600 mt-1">You can try downloading the pack directly from the Report Centre instead.</p>
              )}
            </div>
          )}
          <div>
            <Label className="text-xs">Recipients (comma-separated) *</Label>
            <Input
              value={recipients}
              onChange={e => setRecipients(e.target.value)}
              placeholder="name@client.com, ops@site.com"
              data-testid="send-pack-recipients-input"
            />
          </div>
          <div>
            <Label className="text-xs">Subject</Label>
            <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Report Pack — Site (Session Ref)" data-testid="send-pack-subject-input" />
          </div>
          <div>
            <Label className="text-xs">Message</Label>
            <Textarea rows={3} value={message} onChange={e => setMessage(e.target.value)} data-testid="send-pack-message-input" />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={includePdf} onCheckedChange={setIncludePdf} data-testid="send-pack-include-pdf-checkbox" />
              PDF Pack
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox checked={includeExcel} onCheckedChange={setIncludeExcel} data-testid="send-pack-include-excel-checkbox" />
              Excel Pack
            </label>
          </div>
          <p className="text-[10px] text-muted-foreground">Files under ~10MB are attached directly; larger packs are sent as a secure 14-day download link instead.</p>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} data-testid="send-pack-cancel-btn">Cancel</Button>
          <Button size="sm" onClick={send} disabled={sending} data-testid="send-pack-submit-btn">
            {sending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
            {sending ? 'Generating & Sending…' : 'Send'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
